// ConfirmDialog — système centralisé de confirmation (remplace window.confirm/alert).
// Modale centrée, fond assombri, cohérente avec le design du site.
//
// Usage (hook) :
//   const confirm = useConfirm();
//   if (await confirm({ title, message, confirmText, variant: 'danger' })) { ... }
//
// Usage avec actions métier multiples (ex. "Voir les réservations" / "Archiver") :
//   const r = await confirm({ title, message, actions: [
//     { label: 'Voir les réservations', value: 'view', variant: 'outline' },
//     { label: 'Archiver le trajet', value: 'archive', variant: 'primary' },
//   ]});
//   // r === 'view' | 'archive' | false (annulé)
//
// Usage impératif (hors composant React) : import { confirmDialog } from ...
import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import Button from "../Button/Button";
import "./ConfirmDialog.css";

const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  // Repli sur window.confirm si le provider n'est pas monté (sécurité)
  return ctx || (({ message }) => Promise.resolve(window.confirm(message || "Confirmer ?")));
};

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(null); // { options, resolve }

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => setState({ options, resolve }));
  }, []);

  // Exposer une API impérative globale (pour le code hors hooks)
  useEffect(() => { window.__confirm = confirm; return () => { window.__confirm = null; }; }, [confirm]);

  const close = (value) => {
    if (state) { state.resolve(value); setState(null); }
  };

  // ESC = annuler
  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const o = state?.options || {};
  const actions = Array.isArray(o.actions) && o.actions.length > 0
    ? o.actions
    : [{ label: o.confirmText || (o.alert ? "OK" : "Confirmer"), value: true, variant: o.variant === "danger" ? "danger" : "primary" }];

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div className="cfd-overlay" onClick={() => close(false)} role="dialog" aria-modal="true">
          <div className="cfd-box" onClick={(e) => e.stopPropagation()}>
            <div className={`cfd-icon cfd-icon--${o.variant || "default"}`}>
              {o.variant === "danger" ? "🗑️" : o.variant === "warning" ? "⚠️" : "❓"}
            </div>
            {o.title && <h3 className="cfd-title">{o.title}</h3>}
            {o.message && <div className="cfd-message">{o.message}</div>}
            <div className="cfd-actions">
              {!o.alert && <Button variant="outline" onClick={() => close(false)}>{o.cancelText || "Annuler"}</Button>}
              {actions.map((a, i) => (
                <Button key={i} variant={a.variant || "primary"} onClick={() => close(a.value)}>
                  {a.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

// API impérative (renvoie une Promise) pour les contextes hors composant React.
export const confirmDialog = (options) =>
  window.__confirm ? window.__confirm(options) : Promise.resolve(window.confirm(options?.message || "Confirmer ?"));

export default ConfirmProvider;
